import React, { useState } from "react";
import dayjs from "dayjs";
import OrganizerPillSelect from "../../components/OrganizerPillSelect";
import {
	buildCourtLayers,
	buildRows,
	validateReadySchedule,
	formatAxisMinute,
	minuteToOffset,
	GRID_SLOT_HEIGHT_PX,
	gridItemStyle,
	gridRowStyle,
	isFreeStart,
} from "./dailyCourtGridLayout";
import { blockTypeMeta, blockGroupRange } from "./courtBlockModel";
import { shortSeriesDate } from "./courtSeriesModel";
import { whatsappHref, WhatsappLink } from "../../components/Whatsapp";
import BirthdayInboxLink from "./BirthdayInboxLink";
import { cardLabel, isOpenMatchRow } from "./openMatchModel";
import "./OrganizerDailyCourtGrid.css";

const STATE_MESSAGES = {
	loading: "Cargando agenda",
	error: "No pudimos cargar la agenda",
	configuration_required: "Falta configurar la agenda",
	closed: "El club está cerrado",
	no_active_courts: "No hay canchas activas",
};

// Visual identity per reservation status. Colours resolve to the
// --odcg-status-* tokens declared in OrganizerDailyCourtGrid.css so the grid
// stays aligned with the status badges used in the reservations table.
const STATUS_META = {
	confirmed: { label: "Confirmada", tone: "confirmed" },
	reserved: { label: "Próxima", tone: "reserved" },
	pending: { label: "Pendiente", tone: "pending" },
	holding: { label: "Retenida", tone: "holding" },
	no_show: { label: "No Show", tone: "no-show" },
	cancelled: { label: "Cancelada", tone: "cancelled" },
	expired: { label: "Expirada", tone: "holding" },
	present: { label: "Presente", tone: "present" },
};

const statusMeta = (status) =>
	STATUS_META[status] || { label: status, tone: "holding" };

const isReservationPresent = (reservation) =>
	Boolean(reservation?.checkedInAt) ||
	reservation?.status === "present" ||
	reservation?.checkedIn === true ||
	reservation?.checkIn === true ||
	Boolean(reservation?.checkInAt) ||
	Boolean(reservation?.checkinAt);

const intervalLabel = (item) =>
	`${formatAxisMinute(item.startMin)}-${formatAxisMinute(item.endMin)}`;

const rangeLabel = (item) =>
	`${formatAxisMinute(item.startMin)} a ${formatAxisMinute(item.endMin)}hs`;

const customerName = (item) => item.customer?.name?.trim() || "Sin nombre";

const originLabel = (item) =>
	item.bookingOrigin === "organizer" ? "Carga manual" : "Online";

// Turno fijo: the schedule sends the series id and, when known, the date the
// series ends, so the tag can say how long it lasts.
const seriesTitle = (item) =>
	item.seriesDateTo
		? `Turno fijo hasta el ${shortSeriesDate(item.seriesDateTo)}`
		: "Turno fijo";

function ScheduleState({ state, schedule, onRetry }) {
	const effectiveState =
		state === "loading" || state === "error" ? state : schedule?.state;
	const message = STATE_MESSAGES[effectiveState] || "Agenda no disponible";
	if (effectiveState === "error") {
		return (
			<div className="odcg-state" role="alert">
				<p>{message}</p>
				<button type="button" className="zr-btn zr-btn--soft" onClick={onRetry}>
					Reintentar agenda
				</button>
			</div>
		);
	}
	return (
		<p
			className="odcg-state"
			role={effectiveState === "loading" ? "status" : "note"}
		>
			{message}
		</p>
	);
}

// Pedidos de cumpleaños de la agenda (no ocupan cancha): solo los bien formados y dentro del eje se
// dibujan; cualquier otra cosa se ignora sin romper la grilla.
const BIRTHDAY_MARKER_LABELS = { pending: "Solicitud", proposed: "Propuesta" };
const clockMinute = (value) => {
	const match = /^([0-9]{2}):([0-9]{2})$/.exec(typeof value === "string" ? value : "");
	if (!match) return null;
	const hours = Number(match[1]);
	const minutes = Number(match[2]);
	return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
};
export const birthdayMarkers = (schedule) => {
	const list = Array.isArray(schedule?.birthdayRequests)
		? schedule.birthdayRequests
		: [];
	const axis = schedule?.axis;
	return list.flatMap((entry) => {
		if (!entry || typeof entry !== "object") return [];
		const label = BIRTHDAY_MARKER_LABELS[entry.status];
		const startMin = clockMinute(entry.slotStart);
		let endMin = clockMinute(entry.slotEnd);
		if (!label || !entry.requestId || !entry.courtId || startMin === null) {
			return [];
		}
		if (endMin === null) return [];
		if (endMin <= startMin) endMin += 1440;
		if (startMin < axis.startMin || startMin >= axis.endMin) return [];
		return [
			{
				id: String(entry.requestId),
				courtId: String(entry.courtId),
				label,
				startMin,
				endMin: Math.min(endMin, axis.endMin),
				text: [entry.customerName, entry.packageName]
					.filter((part) => typeof part === "string" && part.trim())
					.join(" · "),
				range: `${entry.slotStart}–${entry.slotEnd}`,
			},
		];
	});
};

function BirthdayMarker({ marker, style }) {
	return (
		<div
			className={`odcg-bday-marker odcg-bday-marker--${marker.label === "Propuesta" ? "proposed" : "pending"}`}
			style={style}
		>
			<BirthdayInboxLink
				requestId={marker.id}
				className="odcg-bday-marker-link"
				aria-label={`${marker.label} de cumpleaños ${marker.range} ${marker.text}`.trim()}
			>
				<span className="odcg-bday-marker-tag">{marker.label}</span>
				<span className="odcg-bday-marker-text">{marker.text || marker.range}</span>
			</BirthdayInboxLink>
		</div>
	);
}

function ReservationCard({ item, style, onSelect, selectedReservationId }) {
	const isPresent = isReservationPresent(item);
	const isSelected =
		selectedReservationId != null &&
		String(selectedReservationId) === String(item.id);
	const meta = statusMeta(isPresent ? "present" : item.status);
	const waHref = whatsappHref(item.customer?.phone);
	const name = customerName(item);
	const notes = item.notes?.trim();
	const birthday =
		item.birthday && typeof item.birthday === "object" ? item.birthday : null;
	const birthdayPerson =
		typeof birthday?.birthdayPerson === "string"
			? birthday.birthdayPerson.trim()
			: "";
	// Partido abierto (Falta un jugador): dashed card with how many players are missing.
	const openMatch = isOpenMatchRow(item) ? item.openMatch : null;
	return (
		<div
			className={`odcg-item odcg-reservation odcg-tone--${meta.tone}${
				isPresent ? " odcg-item--present" : ""
			}${waHref ? " odcg-item--has-wa" : ""}${isSelected ? " odcg-item--selected" : ""}${openMatch ? " odcg-item--open-match" : ""}`}
			style={style}
		>
			<button
				type="button"
				className="odcg-item-main"
				aria-label={`Reserva ${isPresent ? "presente" : item.status} ${intervalLabel(item)}`}
				aria-pressed={isSelected}
				onClick={onSelect}
			>
				<span className="odcg-item-top">
					<span className="odcg-item-dot" aria-hidden="true" />
					<span className="odcg-item-time">{rangeLabel(item)}</span>
					{item.seriesId ? (
						<span className="odcg-item-series" title={seriesTitle(item)}>
							Fijo
						</span>
					) : null}
					{birthday ? (
						<span className="odcg-item-bday" title="Cumpleaños">
							Cumpleaños
						</span>
					) : null}
					{openMatch ? (
						<span className="odcg-item-om" title="Partido abierto">
							Partido abierto
						</span>
					) : null}
					<span className="odcg-item-tag">{meta.label}</span>
				</span>
				{openMatch ? (
					<span className="odcg-item-notes odcg-item-om-players">
						{cardLabel(openMatch)}
					</span>
				) : null}
				{birthdayPerson ? (
					<span className="odcg-item-notes">🎂 {birthdayPerson}</span>
				) : null}
				{notes ? (
					<span className="odcg-item-notes">💬 {notes}</span>
				) : null}
				<span className="odcg-item-bottom">
					<span className="odcg-item-name">{name}</span>
					<span className="odcg-item-origin">{originLabel(item)}</span>
				</span>
			</button>
			<WhatsappLink phone={item.customer?.phone} name={name} />
		</div>
	);
}

// Type and group come from the daily schedule; legacy blocks have neither and
// render as a plain "Bloqueo". Tournament and maintenance get their own tone
// through the --odcg-block-* tokens.
function BlockCard({ item, style, onSelect }) {
	const meta = blockTypeMeta(item.type);
	const reason = item.reason || "Sin motivo";
	const when = item.allDay ? "Todo el día" : intervalLabel(item);
	const groupRange = blockGroupRange(item);
	return (
		<button
			type="button"
			className={`odcg-item odcg-block odcg-block--${meta.tone}`}
			style={style}
			aria-label={`${meta.label} ${reason} ${when}${groupRange ? ` ${groupRange}` : ""}`}
			onClick={onSelect}
		>
			<span className="odcg-item-top">
				<span className="odcg-item-dot" aria-hidden="true" />
				<span className="odcg-item-time">
					{item.allDay ? "Todo el día" : rangeLabel(item)}
				</span>
				<span className="odcg-item-tag">{meta.label}</span>
			</span>
			<span className="odcg-item-bottom">
				<span className="odcg-item-name">{reason}</span>
				{groupRange ? (
					<span className="odcg-item-range">{groupRange}</span>
				) : null}
			</span>
		</button>
	);
}

function HistoryCard({ item, style }) {
	return (
		<span className="odcg-item odcg-history" style={style}>
			<span className="odcg-sr-only">
				{`Historial cancelada ${intervalLabel(item)}`}
			</span>
			<span className="odcg-item-top" aria-hidden="true">
				<span className="odcg-item-dot" />
				<span className="odcg-item-time">{rangeLabel(item)}</span>
				<span className="odcg-item-tag">Cancelada</span>
			</span>
			<span className="odcg-item-bottom" aria-hidden="true">
				<span className="odcg-item-name">{customerName(item)}</span>
			</span>
		</span>
	);
}

// Red "now" marker, only while the grid is showing today and the current time
// falls inside the rendered axis.
const nowOffset = (schedule, pixelsPerMinute) => {
	const now = dayjs();
	if (now.format("YYYY-MM-DD") !== schedule.date) return null;
	const nowMin = now.hour() * 60 + now.minute();
	const { startMin, endMin } = schedule.axis;
	if (nowMin < startMin || nowMin > endMin) return null;
	return minuteToOffset(nowMin, schedule.axis, pixelsPerMinute);
};

export default function OrganizerDailyCourtGrid({
	schedule,
	state = "idle",
	showCancelled = false,
	selectedReservationId = null,
	onRetry = () => {},
	onFreeSelect = () => {},
	onReservationSelect = () => {},
	onBlockSelect = () => {},
}) {
	const [selectedCourtId, setSelectedCourtId] = useState(null);

	if (state === "loading" || state === "error") {
		return (
			<ScheduleState state={state} schedule={schedule} onRetry={onRetry} />
		);
	}
	if (
		!schedule ||
		schedule.state !== "ready" ||
		!schedule.bookable ||
		!schedule.axis
	) {
		return (
			<ScheduleState state={state} schedule={schedule} onRetry={onRetry} />
		);
	}
	if (!validateReadySchedule(schedule).valid) {
		return <ScheduleState state={state} schedule={null} onRetry={onRetry} />;
	}

	const activeCourtId =
		selectedCourtId && schedule.courts.some((c) => String(c.id) === String(selectedCourtId))
			? selectedCourtId
			: schedule.courts[0]?.id;

	const periods = schedule.axis.periods;
	const rows = buildRows(periods, schedule.granularityMinutes);
	const layerResult = buildCourtLayers(schedule, showCancelled);
	if (layerResult.hasUnknownOccupancy) {
		return <ScheduleState state={state} schedule={null} onRetry={onRetry} />;
	}

	const gridColumns = `76px repeat(${schedule.courts.length}, minmax(190px, 1fr))`;
	const rowStyle = (row) => gridRowStyle(row, schedule.granularityMinutes);
	const itemStyle = (item) =>
		gridItemStyle(item, schedule.axis, schedule.granularityMinutes);
	const rawReservation = (placement) =>
		schedule.reservations.find(
			(reservation) => reservation.id === placement.id,
		) || placement;
	const rawBlock = (placement) =>
		schedule.blocks.find((block) => block.id === placement.id) || placement;
	const markers = birthdayMarkers(schedule);
	const nowTop = nowOffset(
		schedule,
		GRID_SLOT_HEIGHT_PX / schedule.granularityMinutes,
	);

	return (
		<section className="odcg" aria-label={`Agenda del ${schedule.date}`}>
			{schedule.reservations.some(isOpenMatchRow) ? (
				<div className="odcg-legend">
					<span className="odcg-legend__item">
						<span
							className="odcg-legend__swatch odcg-legend__swatch--open-match"
							aria-hidden="true"
						/>
						Partido abierto
					</span>
				</div>
			) : null}
			<div
				className="odcg-viewport"
				role="region"
				aria-label="Agenda diaria por cancha"
				tabIndex="0"
			>
				<div
					className="odcg-matrix"
					style={{
						gridTemplateColumns: gridColumns,
						"--odcg-slot-height": `${GRID_SLOT_HEIGHT_PX}px`,
					}}
				>
					<div className="odcg-corner" aria-hidden="true">
						Hora
					</div>
					<div className="odcg-headers" data-testid="court-headers">
						{schedule.courts.map((court) => (
							<div className={`odcg-court-header${String(court.id) === String(activeCourtId) ? " odcg-court-header--active" : ""}`} key={court.id}>
								<h3 className="odcg-court-header-name">{court.name}</h3>
								{schedule.courts.length > 1 && (
									<div className="odcg-court-select-wrap">
										<OrganizerPillSelect
											value={activeCourtId}
											onChange={(val) => setSelectedCourtId(val)}
											options={schedule.courts.map((c) => ({
												value: c.id,
												label: c.name,
											}))}
										/>
									</div>
								)}
							</div>
						))}
					</div>

					<div className="odcg-times">
						{rows.map((row) => (
							<div
								className={`odcg-time${row.startMin % 60 === 0 ? " odcg-time--hour" : ""}${row.type === "gap" ? " odcg-time--gap" : ""}`}
								aria-label={`Hora ${formatAxisMinute(row.startMin)}`}
								key={`${row.type}-${row.startMin}`}
								style={rowStyle(row)}
							>
								{formatAxisMinute(row.startMin)}
							</div>
						))}
						{nowTop === null ? null : (
							<span
								className="odcg-now-dot"
								style={{ top: nowTop }}
								aria-hidden="true"
							/>
						)}
					</div>

					{schedule.courts.map((court) => {
						const layer = layerResult.layers[court.id];
						const isActive = String(court.id) === String(activeCourtId);
						return (
							<div
								className={`odcg-court${isActive ? " odcg-court--active" : ""}`}
								role="group"
								aria-label={`Agenda de ${court.name}`}
								key={court.id}
							>
								<div className="odcg-slots">
									{rows.map((row) => (
										<div
											className={`odcg-slot${row.startMin % 60 === 0 ? " odcg-slot--hour" : ""}${row.type === "gap" ? " odcg-slot--gap" : ""}`}
											key={`${row.type}-${row.startMin}`}
											style={rowStyle(row)}
										>
											{row.type === "row" &&
											isFreeStart(
												court.id,
												row.startMin,
												row.endMin,
												periods,
												layerResult,
											) ? (
												<button
													type="button"
													className="odcg-free"
													aria-label={`Libre ${court.name} ${formatAxisMinute(row.startMin)}`}
													onClick={() =>
														onFreeSelect({
															courtId: court.id,
															date: schedule.date,
															startMin: row.startMin,
															endMin: row.endMin,
														})
													}
												>
													<span className="odcg-free-plus" aria-hidden="true">
														+
													</span>
												</button>
											) : null}
										</div>
									))}
								</div>

								<div className="odcg-items">
									{nowTop === null ? null : (
										<span
											className="odcg-now-line"
											style={{ top: nowTop }}
											aria-hidden="true"
										/>
									)}
									{layer.occupancy.map((item) =>
										item.kind === "reservation" ? (
											<ReservationCard
												key={item.id}
													item={item}
													style={itemStyle(item)}
													selectedReservationId={selectedReservationId}
													onSelect={() => onReservationSelect(rawReservation(item))}
											/>
										) : (
											<BlockCard
												key={`${item.id}-${court.id}-${item.startMin}`}
												item={item}
												style={itemStyle(item)}
												onSelect={() => onBlockSelect(rawBlock(item))}
											/>
										),
									)}
									{markers
										.filter((marker) => marker.courtId === String(court.id))
										.map((marker) => (
											<BirthdayMarker
												key={marker.id}
												marker={marker}
												style={itemStyle(marker)}
											/>
										))}
									{layer.history.map((item) => (
										<HistoryCard
											key={item.id}
											item={item}
											style={itemStyle(item)}
										/>
									))}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</section>
	);
}
